import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const LAST_UPDATED_ISO = '2026-05-27';

const styles = {
  page: { minHeight: '100dvh', background: '#0F172A', color: '#F8FAFC', fontFamily: 'system-ui, -apple-system, sans-serif', padding: '3rem 1.5rem' },
  container: { maxWidth: 820, margin: '0 auto' },
  backLink: { color: '#60A5FA', textDecoration: 'none', fontSize: '0.9rem', display: 'inline-block', marginBottom: '2rem' },
  h1: { fontSize: '2rem', fontWeight: 800, color: '#F8FAFC', marginBottom: '0.5rem' },
  updated: { color: '#94A3B8', fontSize: '0.85rem', marginBottom: '2.5rem' },
  h2: { fontSize: '1.2rem', fontWeight: 700, color: '#F8FAFC', marginTop: '2rem', marginBottom: '0.75rem' },
  h3: { fontSize: '1rem', fontWeight: 600, color: '#E2E8F0', marginTop: '1.25rem', marginBottom: '0.5rem' },
  p: { color: '#CBD5E1', lineHeight: 1.7, marginBottom: '1rem', fontSize: '0.95rem' },
  ul: { color: '#CBD5E1', lineHeight: 1.7, paddingLeft: '1.5rem', marginBottom: '1rem', fontSize: '0.95rem' },
  table: { width: '100%', borderCollapse: 'collapse', marginBottom: '1.5rem', fontSize: '0.9rem' },
  th: { textAlign: 'left', padding: '0.6rem 1rem', borderBottom: '1px solid #334155', color: '#F8FAFC', fontWeight: 600 },
  td: { padding: '0.6rem 1rem', borderBottom: '1px solid #1E293B', color: '#CBD5E1', verticalAlign: 'top' },
  link: { color: '#60A5FA', textDecoration: 'none' },
  printBtn: { float: 'right', padding: '0.5rem 1rem', fontSize: '0.85rem', border: '1px solid #334155', background: '#1E293B', color: '#E2E8F0', borderRadius: 6, cursor: 'pointer' },
};

export default function DpaPage() {
  const { t, i18n } = useTranslation();
  const lastUpdated = new Date(LAST_UPDATED_ISO).toLocaleDateString(i18n.language, {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <Link to="/" style={styles.backLink}>{t('legal.back')}</Link>
        <button type="button" style={styles.printBtn} onClick={() => window.print()}>
          {t('legal.dpa.print')}
        </button>

        <h1 style={styles.h1}>{t('legal.dpa.title')}</h1>
        <p style={styles.updated}>{t('legal.lastUpdated', { date: lastUpdated })}</p>

        {/* English-only body per the Privacy/Terms convention. */}

        <p style={styles.p}>
          This Data Processing Agreement ("DPA") governs the processing of
          personal data by vQueue ("Processor") on behalf of the business
          customer ("Controller") that subscribes to the vQueue Service. It
          forms part of, and is incorporated into, the Terms of Service
          between the parties. By creating a vQueue workspace the Controller
          accepts the terms of this DPA; a counter-signed copy is available on
          request at <a href="mailto:privacy@waitless.live" style={styles.link}>privacy@waitless.live</a>.
        </p>

        <h2 style={styles.h2}>1. Roles and responsibilities</h2>
        <p style={styles.p}>
          In respect of end-user queue participants (the Controller's
          customers), the Controller is the data controller and the Processor
          acts solely as a processor within the meaning of Article 4 GDPR. The
          Processor will process personal data only on documented instructions
          from the Controller, which instructions are given through the
          configuration of the Service and this DPA.
        </p>

        <h2 style={styles.h2}>2. Subject matter, duration, nature, and purpose</h2>
        <p style={styles.p}>
          The Processor processes personal data for the sole purpose of
          providing the virtual-queue service — accepting queue joins,
          maintaining real-time queue state, notifying customers when called,
          and producing anonymised analytics for the Controller. Processing
          lasts for the term of the Controller's subscription and the
          retention periods set out in clause 6 below.
        </p>

        <h2 style={styles.h2}>3. Categories of data and data subjects</h2>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Data subject</th>
              <th style={styles.th}>Categories of personal data</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={styles.td}>End-user queue participants</td>
              <td style={styles.td}>First name, emoji avatar, ticket metadata (join/call/complete times, wait duration), Web Push subscription endpoint (if opted in)</td>
            </tr>
            <tr>
              <td style={styles.td}>Walk-up kiosk participants</td>
              <td style={styles.td}>None — fully anonymous sequential ticket number only</td>
            </tr>
            <tr>
              <td style={styles.td}>Controller's authorised users (staff)</td>
              <td style={styles.td}>Email address, hashed password, role, login timestamps, IP address, user-agent</td>
            </tr>
          </tbody>
        </table>

        <h2 style={styles.h2}>4. Processor obligations</h2>
        <ul style={styles.ul}>
          <li>Process personal data only on documented instructions from the Controller, including transfers outside the EEA.</li>
          <li>Ensure that personnel authorised to process the data are bound by appropriate confidentiality obligations.</li>
          <li>Implement appropriate technical and organisational measures as set out in clause 7 (Annex — Security Measures).</li>
          <li>Assist the Controller, so far as possible, in responding to data-subject requests and in meeting its obligations under Articles 32 to 36 GDPR.</li>
          <li>Make available to the Controller all information necessary to demonstrate compliance with this DPA and allow for audits in accordance with clause 8.</li>
          <li>Notify the Controller without undue delay, and no later than 72 hours, after becoming aware of a personal-data breach affecting Controller data.</li>
        </ul>

        <h2 style={styles.h2}>5. Sub-processors</h2>
        <p style={styles.p}>
          The Controller grants general authorisation for the use of
          sub-processors. The current sub-processors are:
        </p>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Sub-processor</th>
              <th style={styles.th}>Purpose</th>
              <th style={styles.th}>Location</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={styles.td}>Hetzner Online GmbH</td><td style={styles.td}>Hosting of the application servers, reverse proxy, and monitoring stack</td><td style={styles.td}>EU (Germany / Finland)</td></tr>
            <tr><td style={styles.td}>Neon, Inc.</td><td style={styles.td}>Managed PostgreSQL (primary data store)</td><td style={styles.td}>EU (Frankfurt)</td></tr>
            <tr><td style={styles.td}>Upstash, Inc.</td><td style={styles.td}>Managed Redis (queue state, refresh tokens, rate limits)</td><td style={styles.td}>EU</td></tr>
            <tr><td style={styles.td}>Resend, Inc.</td><td style={styles.td}>Transactional email delivery (verification, reset, staff invites)</td><td style={styles.td}>EU / USA (SCCs)</td></tr>
            <tr><td style={styles.td}>Stripe Payments Europe, Ltd.</td><td style={styles.td}>Subscription billing and payment processing</td><td style={styles.td}>Ireland / USA (SCCs)</td></tr>
            <tr><td style={styles.td}>Cloudflare, Inc.</td><td style={styles.td}>Bot-protection (Turnstile)</td><td style={styles.td}>USA (SCCs)</td></tr>
            <tr><td style={styles.td}>Functional Software, Inc. (Sentry)</td><td style={styles.td}>Application error monitoring (IP, email, request body, auth headers stripped before send)</td><td style={styles.td}>EU</td></tr>
            <tr><td style={styles.td}>Browser push services (Mozilla, Google, Apple)</td><td style={styles.td}>Delivery of encrypted Web Push payloads</td><td style={styles.td}>As operated by the respective browser vendor</td></tr>
          </tbody>
        </table>
        <p style={styles.p}>
          The Processor will give the Controller at least 30 days' prior
          notice by email of any intended addition or replacement of
          sub-processors. The Controller may object on reasonable grounds
          related to data protection, in which case the parties will work in
          good faith to agree on a resolution; if none is reached, the
          Controller may terminate the subscription with no further liability.
        </p>

        <h2 style={styles.h2}>6. Retention and deletion</h2>
        <ul style={styles.ul}>
          <li>Live queue session data (first name, avatar): deleted at the end of the session, at the latest 24 hours after joining.</li>
          <li>Push subscriptions: deleted on ticket completion or cancellation, or on a browser <code>410 Gone</code> response.</li>
          <li>Historical ticket identifiers: removed by an automated daily scrub 30 days after ticket completion; aggregated, non-identifying analytics may be retained indefinitely.</li>
          <li>Controller staff accounts, login sessions, and audit log: retained while the subscription is active and then deleted on request.</li>
          <li>On termination of the subscription, the Processor will, at the Controller's choice, return or delete all Controller data within 30 days, save where retention is required by applicable law.</li>
        </ul>

        <h2 style={styles.h2}>7. Security measures</h2>
        <ul style={styles.ul}>
          <li>TLS 1.2+ for all data in transit; HSTS with preload.</li>
          <li>Passwords hashed with bcrypt; secrets injected via environment variables.</li>
          <li>Strict tenant isolation enforced in every service-layer query.</li>
          <li>Role-based access control (OWNER / MANAGER / STAFF / ADMIN) with server-side enforcement.</li>
          <li>Rate limiting on authentication, signup, password-reset, and public endpoints.</li>
          <li>Automated daily anonymisation of historical ticket identifiers after 30 days.</li>
          <li>Audit log of privileged administrative actions.</li>
          <li>Encrypted database and Redis volumes at the infrastructure layer; regular backups with restricted access.</li>
        </ul>

        <h2 style={styles.h2}>8. Audit rights</h2>
        <p style={styles.p}>
          The Processor will, on reasonable prior notice and no more than once
          per calendar year (except where a regulator or a personal-data
          breach justifies more), make available to the Controller the
          documentation needed to demonstrate compliance with this DPA,
          including sub-processor lists, pen-test summaries, and the result
          of any relevant third-party audits. On-site audits may be conducted
          by the Controller or its independent auditor subject to a reasonable
          written scope and mutual confidentiality undertakings.
        </p>

        <h2 style={styles.h2}>9. International transfers</h2>
        <p style={styles.p}>
          Where a sub-processor is located outside the EEA, the Processor
          relies on the European Commission's Standard Contractual Clauses
          (2021/914) or an equivalent approved transfer mechanism. A copy of
          the applicable SCCs is available on request.
        </p>

        <h2 style={styles.h2}>10. Liability and governing law</h2>
        <p style={styles.p}>
          Each party's liability under this DPA is subject to the limitations
          and exclusions of liability in the Terms of Service between the
          parties. This DPA is governed by the law of the Controller's
          primary place of establishment within the EEA, or, failing that,
          the law of the Processor's place of establishment.
        </p>

        <h2 style={styles.h2}>11. Contact</h2>
        <p style={styles.p}>
          Questions and notices under this DPA, including sub-processor
          objections and data-subject requests forwarded by the Controller,
          go to <a href="mailto:privacy@waitless.live" style={styles.link}>privacy@waitless.live</a>.
          The Processor will acknowledge within 5 business days.
        </p>

        <p style={{ ...styles.p, marginTop: '2rem', borderTop: '1px solid #1E293B', paddingTop: '1.5rem' }}>
          See also our <Link to="/privacy" style={styles.link}>Privacy Policy</Link>
          <> </>and <Link to="/terms" style={styles.link}>Terms of Service</Link>.
        </p>
      </div>
    </div>
  );
}
